import { createFileRoute } from "@tanstack/react-router";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { useSession, embeddedAwareSignOut } from "@/lib/auth-client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowLeft, Save, Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { BACKEND_URL } from "../config/env";
import { Helmet } from "react-helmet-async";
import { DataExportPanel } from "@/components/settings/DataExportPanel";

const BASE = BACKEND_URL || "";

export const Route = createFileRoute("/settings")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <ProtectedRoute>
      <SettingsContent />
    </ProtectedRoute>
  );
}

function SettingsContent() {
  const { data: session } = useSession();
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function handleDeleteAccount() {
    const ok = window.confirm(
      "this will permanently delete your account and all data. continue?"
    );
    if (!ok) return;
    try {
      setDeleting(true);
      const res = await fetch(`${BASE}/api/account`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        // simple alert to avoid adding extra dependencies here
        alert("failed to delete account");
        setDeleting(false);
        return;
      }
      // best effort sign out to clear cookies
      try {
        await embeddedAwareSignOut();
      } catch {
        // ignore sign out errors during account deletion
      }
      window.location.href = "/";
    } catch {
      alert("failed to delete account");
      setDeleting(false);
    }
  }

  if (!session) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 p-4">
      <Helmet>
        <title>settings</title>
        <meta name="robots" content="noindex,nofollow,noarchive,noimageindex" />
      </Helmet>
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-20 left-20 text-amber-400/30 text-2xl animate-pulse">
          ✦
        </div>
        <div
          className="absolute top-40 right-32 text-amber-500/20 text-xl animate-bounce"
          style={{ animationDelay: "0.5s" }}
        >
          ✧
        </div>
        <div
          className="absolute bottom-32 left-40 text-orange-400/25 text-lg animate-pulse"
          style={{ animationDelay: "1s" }}
        >
          ✦
        </div>
        <div
          className="absolute top-60 left-1/2 text-yellow-400/20 text-xl animate-bounce"
          style={{ animationDelay: "1.5s" }}
        >
          ✧
        </div>
        <div
          className="absolute bottom-40 right-20 text-amber-400/30 text-2xl animate-pulse"
          style={{ animationDelay: "2s" }}
        >
          ✦
        </div>
      </div>

      <div className="max-w-2xl mx-auto space-y-8">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            className="rounded-xl"
            onClick={() => (window.location.href = "/dashboard")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">settings</h1>
            <p className="text-gray-600 mt-1">
              manage your account and preferences
            </p>
          </div>
        </div>

        <Card className="bg-white/70 backdrop-blur-sm border-amber-200/50 rounded-3xl shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl text-gray-900">
              profile information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center gap-4">
              <Avatar className="h-20 w-20 border-2 border-amber-200">
                <AvatarImage src={session.user.image || undefined} />
                <AvatarFallback className="bg-amber-100 text-amber-800 text-xl font-semibold">
                  {session.user.name?.charAt(0).toUpperCase() ||
                    session.user.email.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <Button variant="outline" size="sm" className="rounded-xl">
                  change avatar
                </Button>
                <p className="text-sm text-gray-600 mt-1">
                  jpg, png or gif. max 2mb.
                </p>
              </div>
            </div>

            <Separator className="bg-amber-200/50" />

            <div className="space-y-2">
              <Label htmlFor="name" className="text-gray-700">
                display name
              </Label>
              <Input
                id="name"
                defaultValue={session.user.name || ""}
                placeholder="enter your display name"
                className="rounded-xl border-amber-200/50 focus:border-amber-400"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700">
                email address
              </Label>
              <Input
                id="email"
                type="email"
                defaultValue={session.user.email}
                className="rounded-xl border-amber-200/50 focus:border-amber-400"
              />
              <p className="text-sm text-gray-600">
                used for sign in and notifications
              </p>
            </div>

            <div className="flex justify-end">
              <Button className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl gap-2">
                <Save className="h-4 w-4" />
                save changes
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/70 backdrop-blur-sm border-amber-200/50 rounded-3xl shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl text-gray-900">
              change password
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="current-password" className="text-gray-700">
                current password
              </Label>
              <div className="relative">
                <Input
                  id="current-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="enter current password"
                  className="rounded-xl border-amber-200/50 focus:border-amber-400 pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute inset-y-0 right-0 flex items-center justify-center pr-3 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-gray-700">
                new password
              </Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="enter new password"
                  className="rounded-xl border-amber-200/50 focus:border-amber-400 pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute inset-y-0 right-0 flex items-center justify-center pr-3 hover:bg-transparent"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="text-gray-700">
                confirm new password
              </Label>
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="confirm new password"
                  className="rounded-xl border-amber-200/50 focus:border-amber-400 pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute inset-y-0 right-0 flex items-center justify-center pr-3 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="flex justify-end">
              <Button className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl gap-2">
                <Save className="h-4 w-4" />
                update password
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/70 backdrop-blur-sm border-amber-200/50 rounded-3xl shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl">data management</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <DataExportPanel />
          </CardContent>
        </Card>

        <Card className="bg-white/70 backdrop-blur-sm border-red-200/50 rounded-3xl shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl text-red-700">danger zone</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-red-50/50 rounded-2xl border border-red-200/50">
              <div>
                <h3 className="font-semibold text-red-700">delete account</h3>
                <p className="text-sm text-red-600">
                  permanently delete your account and all data
                </p>
              </div>
              <Button
                variant="destructive"
                size="sm"
                className="rounded-xl"
                disabled={deleting}
                onClick={handleDeleteAccount}
              >
                delete account
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
